import React, { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../config";
import ResimUpload from "../ResimUpload";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Eye,
  Heart,
  Image as ImageIcon,
  Video,
  Music,
  MapPin,
  BookOpen,
  X,
  Send,
  Youtube,
  Globe,
  Sparkles,
  Calendar,
  Type,
  CheckCircle,
  XCircle,
} from "lucide-react";

const LANGS = [
  { code: "tr", label: "Türkçe" },
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
];

const t = {
  tr: {
    title: "Mezarlıklar / Dijital Anıtlar",
    addMemorial: "Yeni Anıt Ekle",
    editMemorial: "Anıt Düzenle",
    deleteMemorial: "Anıt Sil",
    confirmDelete: "Bu anıtı silmek istediğinize emin misiniz?",
    fullname: "Ad Soyad",
    birthDate: "Doğum Tarihi",
    birthDateText: "Doğum Tarihi (Metin)",
    birthDatePicker: "Doğum Tarihi (Takvim)",
    deathDate: "Vefat Tarihi",
    deathDateText: "Vefat Tarihi (Metin)",
    deathDatePicker: "Vefat Tarihi (Takvim)",
    birthPlaceholder: "Örn: 15 Mayıs 1950, İstanbul",
    deathPlaceholder: "Örn: 20 Ocak 2024, Ankara",
    city: "Şehir",
    cemeteryName: "Mezarlık Adı",
    graveNo: "Mezar No",
    familyNote: "Aile Notu",
    photo: "Profil Fotoğrafı",
    uploadPhoto: "Fotoğraf Yükle",
    location: "Konum",
    locationAddress: "Google Maps Adresi / Konum",
    locationHelp:
      "Google Maps'ten paylaş butonu ile kopyaladığınız linki yapıştırın",
    status: "Durum",
    active: "Aktif",
    inactive: "Pasif",
    save: "Kaydet",
    cancel: "İptal",
    search: "Ara...",
    all: "Tümü",
    gallery: "Galeri",
    condolences: "Anı Defteri",
    inOurHearts: "Kalbimizdesin",
    viewCount: "görüntülenme",
    condolenceCount: "mesaj",
    mediaCount: "medya",
    noData: "Henüz veri yok",
    loading: "Yükleniyor...",
    lifeStories: "Hayat Hikayesi",
    addStory: "+ Yeni Bölüm Ekle",
    storyTitle: "Bölüm Başlığı",
    storyContent: "İçerik",
    storyPlaceholder:
      "Çocukluğu, gençliği, askerliği, evliliği, iş hayatı, başarıları...",
    religious: "Dini İçerikler",
    addReligiousItem: "+ Yeni Dini İçerik Ekle",
    religiousType: "Tür",
    religiousTypeQuran: "📖 Kuran-ı Kerim",
    religiousTypeHadith: "📜 Hadis-i Şerif",
    religiousTypeNote: "🙏 Dua / Not",
    religiousLink: "Link (YouTube için)",
    religiousText: "Metin / İçerik",
    inOurHeartsTitle: "Kalbimizdesin",
    addMemory: "+ Yeni Anı Ekle",
    memoryTitle: "Başlık",
    memoryDescription: "Açıklama",
    memoryImage: "Görsel",
    condolencePlaceholder: "Taziye mesajınızı yazın...",
    sendCondolence: "Mesaj Gönder",
    yourName: "Adınız",
    pendingApproval: "Onay Bekliyor",
    approved: "Onaylandı",
    rejected: "Reddedildi",
    approve: "Onayla",
    reject: "Reddet",
    deleteCondolence: "Mesajı Sil",
    basicInfo: "Temel Bilgiler",
    mapPreview: "Harita Görünümü",
    viewOnMap: "Haritada Aç",
    send: "Gönder",
    close: "Kapat",
    details: "Detaylar",
    writeHere: "Buraya yazın...",
    saveSuccess: "✅ Başarıyla kaydedildi",
    saveError: "❌ Kayıt sırasında hata oluştu",
    deleteSuccess: "✅ Anıt silindi",
    deleteError: "❌ Silme sırasında hata oluştu",
    approveSuccess: "✅ Mesaj onaylandı",
    rejectSuccess: "❌ Mesaj reddedildi",
    deleteCondolenceSuccess: "✅ Mesaj silindi",
  },

  en: {
    title: "Cemeteries / Digital Memorials",
    addMemorial: "Add Memorial",
    editMemorial: "Edit Memorial",
    deleteMemorial: "Delete Memorial",
    confirmDelete: "Are you sure you want to delete this memorial?",
    fullname: "Full Name",
    birthDate: "Birth Date",
    birthDateText: "Birth Date (Text)",
    birthDatePicker: "Birth Date (Calendar)",
    deathDate: "Death Date",
    deathDateText: "Death Date (Text)",
    deathDatePicker: "Death Date (Calendar)",
    birthPlaceholder: "e.g. May 15, 1950, Istanbul",
    deathPlaceholder: "e.g. Jan 20, 2024, Ankara",
    city: "City",
    cemeteryName: "Cemetery Name",
    graveNo: "Grave No",
    familyNote: "Family Note",
    photo: "Profile Photo",
    uploadPhoto: "Upload Photo",
    location: "Location",
    locationAddress: "Google Maps Address / Location",
    locationHelp: "Paste the link you copied from Google Maps share button",
    status: "Status",
    active: "Active",
    inactive: "Inactive",
    save: "Save",
    cancel: "Cancel",
    search: "Search...",
    all: "All",
    gallery: "Gallery",
    condolences: "Guest Book",
    inOurHearts: "In Our Hearts",
    viewCount: "views",
    condolenceCount: "messages",
    mediaCount: "media",
    noData: "No data yet",
    loading: "Loading...",
    lifeStories: "Life Story",
    addStory: "+ Add New Chapter",
    storyTitle: "Chapter Title",
    storyContent: "Content",
    storyPlaceholder:
      "Childhood, youth, military service, marriage, career, achievements...",
    religious: "Religious Content",
    addReligiousItem: "+ Add Religious Content",
    religiousType: "Type",
    religiousTypeQuran: "📖 Quran",
    religiousTypeHadith: "📜 Hadith",
    religiousTypeNote: "🙏 Prayer / Note",
    religiousLink: "Link (for YouTube)",
    religiousText: "Text / Content",
    inOurHeartsTitle: "In Our Hearts",
    addMemory: "+ Add Memory",
    memoryTitle: "Title",
    memoryDescription: "Description",
    memoryImage: "Image",
    condolencePlaceholder: "Write your condolence message...",
    sendCondolence: "Send Message",
    yourName: "Your Name",
    pendingApproval: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    approve: "Approve",
    reject: "Reject",
    deleteCondolence: "Delete Message",
    basicInfo: "Basic Information",
    mapPreview: "Map Preview",
    viewOnMap: "View on Map",
    send: "Send",
    close: "Close",
    details: "Details",
    writeHere: "Write here...",
    saveSuccess: "✅ Saved successfully",
    saveError: "❌ Error while saving",
    deleteSuccess: "✅ Memorial deleted",
    deleteError: "❌ Error while deleting",
    approveSuccess: "✅ Message approved",
    rejectSuccess: "❌ Message rejected",
    deleteCondolenceSuccess: "✅ Message deleted",
  },

  de: {
    title: "Friedhöfe / Digitale Denkmäler",
    addMemorial: "Denkmal hinzufügen",
    editMemorial: "Denkmal bearbeiten",
    deleteMemorial: "Denkmal löschen",
    confirmDelete: "Möchten Sie dieses Denkmal wirklich löschen?",
    fullname: "Vollständiger Name",
    birthDate: "Geburtsdatum",
    birthDateText: "Geburtsdatum (Text)",
    birthDatePicker: "Geburtsdatum (Kalender)",
    deathDate: "Sterbedatum",
    deathDateText: "Sterbedatum (Text)",
    deathDatePicker: "Sterbedatum (Kalender)",
    birthPlaceholder: "z.B. 15. Mai 1950, Istanbul",
    deathPlaceholder: "z.B. 20. Januar 2024, Ankara",
    city: "Stadt",
    cemeteryName: "Friedhofsname",
    graveNo: "Grabnummer",
    familyNote: "Familiennotiz",
    photo: "Profilfoto",
    uploadPhoto: "Foto hochladen",
    location: "Standort",
    locationAddress: "Google Maps Adresse / Standort",
    locationHelp:
      "Fügen Sie den Link ein, den Sie über die Teilen-Schaltfläche von Google Maps kopiert haben",
    status: "Status",
    active: "Aktiv",
    inactive: "Inaktiv",
    save: "Speichern",
    cancel: "Abbrechen",
    search: "Suchen...",
    all: "Alle",
    gallery: "Galerie",
    condolences: "Gästebuch",
    inOurHearts: "In unseren Herzen",
    viewCount: "Aufrufe",
    condolenceCount: "Nachrichten",
    mediaCount: "Medien",
    noData: "Keine Daten",
    loading: "Laden...",
    lifeStories: "Lebensgeschichte",
    addStory: "+ Neuen Abschnitt hinzufügen",
    storyTitle: "Abschnittstitel",
    storyContent: "Inhalt",
    storyPlaceholder:
      "Kindheit, Jugend, Militärdienst, Heirat, Karriere, Erfolge...",
    religious: "Religiöse Inhalte",
    addReligiousItem: "+ Religiösen Inhalt hinzufügen",
    religiousType: "Typ",
    religiousTypeQuran: "📖 Koran",
    religiousTypeHadith: "📜 Hadith",
    religiousTypeNote: "🙏 Gebet / Notiz",
    religiousLink: "Link (für YouTube)",
    religiousText: "Text / Inhalt",
    inOurHeartsTitle: "In unseren Herzen",
    addMemory: "+ Erinnerung hinzufügen",
    memoryTitle: "Titel",
    memoryDescription: "Beschreibung",
    memoryImage: "Bild",
    condolencePlaceholder: "Schreiben Sie Ihre Beileidsnachricht...",
    sendCondolence: "Nachricht senden",
    yourName: "Ihr Name",
    pendingApproval: "Ausstehend",
    approved: "Genehmigt",
    rejected: "Abgelehnt",
    approve: "Genehmigen",
    reject: "Ablehnen",
    deleteCondolence: "Nachricht löschen",
    basicInfo: "Grundinformationen",
    mapPreview: "Kartenvorschau",
    viewOnMap: "Auf Karte anzeigen",
    send: "Senden",
    close: "Schließen",
    details: "Details",
    writeHere: "Hier schreiben...",
    saveSuccess: "✅ Erfolgreich gespeichert",
    saveError: "❌ Fehler beim Speichern",
    deleteSuccess: "✅ Denkmal gelöscht",
    deleteError: "❌ Fehler beim Löschen",
    approveSuccess: "✅ Nachricht genehmigt",
    rejectSuccess: "❌ Nachricht abgelehnt",
    deleteCondolenceSuccess: "✅ Nachricht gelöscht",
  },
};

const getLang = () => {
  const fromApp = localStorage.getItem("app_lang") || "tr";
  return ["tr", "en", "de"].includes(fromApp) ? fromApp : "tr";
};

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;

  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;

  const match = url.match(regExp);
  const videoId = match && match[2].length === 11 ? match[2] : null;

  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }

  return url.includes("youtube.com/embed/") ? url : null;
};

const getGoogleMapsEmbedUrl = (url) => {
  if (!url) return null;

  if (
    url.includes("maps.google.com") ||
    url.includes("google.com/maps")
  ) {
    if (url.includes("/embed")) return url;

    const match = url.match(/q=([^&]+)/);

    if (match) {
      return `https://www.google.com/maps/embed/v1/place?key=AIzaSyDummyKey&q=${encodeURIComponent(
        match[1],
      )}`;
    }
  }

  return url;
};

export default function MezarliklarPage() {
  const [lang, setLang] = useState(getLang);
  const TT = t[lang];

  const [memorials, setMemorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedMemorial, setSelectedMemorial] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const [newStory, setNewStory] = useState({
    title: "",
    content: "",
  });

  const [newReligious, setNewReligious] = useState({
    type: "quran",
    link: "",
    text: "",
  });

  const [newMemory, setNewMemory] = useState({
    title: "",
    description: "",
    image_url: "",
  });

  const [newCondolence, setNewCondolence] = useState({
    sender_name: "",
    message: "",
  });

  const [submittingCondolence, setSubmittingCondolence] =
    useState(false);

  const [formData, setFormData] = useState({
    fullname: "",
    birth_date_text: "",
    birth_date_picker: "",
    death_date_text: "",
    death_date_picker: "",
    city: "",
    cemetery_name: "",
    grave_no: "",
    family_note: "",
    life_stories: [],
    religious_items: [],
    memories: [],
    photo_url: "",
    location_address: "",
    status: "active",
  });

  useEffect(() => {
    const handleLangChange = () => setLang(getLang());

    window.addEventListener(
      "app_lang_change",
      handleLangChange,
    );

    return () =>
      window.removeEventListener(
        "app_lang_change",
        handleLangChange,
      );
  }, []);

  const fetchMemorials = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) return;

    setLoading(true);

    try {
      let url = `${API_BASE}/cemetery/memorials?limit=100`;

      if (statusFilter !== "all") {
        url += `&status=${statusFilter}`;
      }

      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (data.ok) {
        setMemorials(data.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) return;

    try {
      const res = await fetch(
        `${API_BASE}/cemetery/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (data.ok) {
        setStats(data.data);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchMemorials();
    fetchStats();
  }, [fetchMemorials, fetchStats]);

  const handleSave = async () => {
    if (!formData.fullname.trim()) {
      alert(
        TT.fullname +
          " " +
          (lang === "tr" ? "zorunludur" : "is required"),
      );
      return;
    }

    setSaving(true);

    const token = localStorage.getItem("token");

    const birth_date =
      formData.birth_date_picker || formData.birth_date_text;

    const death_date =
      formData.death_date_picker || formData.death_date_text;

    const payload = {
      ...formData,
      birth_date,
      death_date,
    };

    try {
      let url = `${API_BASE}/cemetery/memorials`;
      let method = "POST";

      if (selectedMemorial) {
        url = `${API_BASE}/cemetery/memorials/${selectedMemorial.id}`;
        method = "PUT";
      }

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.ok) {
        setShowModal(false);
        setSelectedMemorial(null);
        resetForm();
        fetchMemorials();
        fetchStats();
        alert(TT.saveSuccess);
      } else {
        alert(data.message || TT.saveError);
      }
    } catch (err) {
      console.error(err);
      alert(TT.saveError);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () =>
    setFormData({
      fullname: "",
      birth_date_text: "",
      birth_date_picker: "",
      death_date_text: "",
      death_date_picker: "",
      city: "",
      cemetery_name: "",
      grave_no: "",
      family_note: "",
      life_stories: [],
      religious_items: [],
      memories: [],
      photo_url: "",
      location_address: "",
      status: "active",
    });

  const handleEdit = (memorial) => {
    setSelectedMemorial(memorial);

    setFormData({
      fullname: memorial.fullname || "",
      birth_date_text: memorial.birth_date_text || "",
      birth_date_picker: memorial.birth_date_picker || "",
      death_date_text: memorial.death_date_text || "",
      death_date_picker: memorial.death_date_picker || "",
      city: memorial.city || "",
      cemetery_name: memorial.cemetery_name || "",
      grave_no: memorial.grave_no || "",
      family_note: memorial.family_note || "",
      life_stories: memorial.life_stories || [],
      religious_items: memorial.religious_items || [],
      memories: memorial.memories || [],
      photo_url: memorial.photo_url || "",
      location_address: memorial.location_address || "",
      status: memorial.status || "active",
    });

    setShowModal(true);
  };

  const handleDelete = async (memorial) => {
    if (
      !window.confirm(
        `${memorial.fullname} - ${TT.confirmDelete}`,
      )
    ) {
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${API_BASE}/cemetery/memorials/${memorial.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (data.ok) {
        fetchMemorials();
        fetchStats();
        alert(TT.deleteSuccess);
      } else {
        alert(data.message || TT.deleteError);
      }
    } catch (err) {
      console.error(err);
      alert(TT.deleteError);
    }
  };

  const handleApproveCondolence = async (
    condolenceId,
    memorialId,
  ) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${API_BASE}/cemetery/condolences/${condolenceId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: "approved",
          }),
        },
      );

      const data = await res.json();

      if (data.ok) {
        alert(TT.approveSuccess);

        const refreshRes = await fetch(
          `${API_BASE}/cemetery/memorials/${memorialId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const refreshData = await refreshRes.json();

        if (refreshData.ok) {
          setShowDetailModal(refreshData.data);
        }

        fetchMemorials();
      }
    } catch (err) {
      console.error(err);
      alert("Hata oluştu");
    }
  };

  const handleRejectCondolence = async (
    condolenceId,
    memorialId,
  ) => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${API_BASE}/cemetery/condolences/${condolenceId}/status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: "rejected",
          }),
        },
      );

      const data = await res.json();

      if (data.ok) {
        alert(TT.rejectSuccess);

        const refreshRes = await fetch(
          `${API_BASE}/cemetery/memorials/${memorialId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const refreshData = await refreshRes.json();

        if (refreshData.ok) {
          setShowDetailModal(refreshData.data);
        }

        fetchMemorials();
      }
    } catch (err) {
      console.error(err);
      alert("Hata oluştu");
    }
  };

  const handleDeleteCondolence = async (
    condolenceId,
    memorialId,
  ) => {
    if (
      !window.confirm(
        "Bu mesajı silmek istediğinize emin misiniz?",
      )
    ) {
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const res = await fetch(
        `${API_BASE}/cemetery/condolences/${condolenceId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await res.json();

      if (data.ok) {
        alert(TT.deleteCondolenceSuccess);

        const refreshRes = await fetch(
          `${API_BASE}/cemetery/memorials/${memorialId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const refreshData = await refreshRes.json();

        if (refreshData.ok) {
          setShowDetailModal(refreshData.data);
        }

        fetchMemorials();
      }
    } catch (err) {
      console.error(err);
      alert("Hata oluştu");
    }
  };

  const addLifeStory = () => {
    if (!newStory.title.trim()) {
      alert(
        TT.storyTitle +
          " " +
          (lang === "tr" ? "giriniz" : "required"),
      );
      return;
    }

    setFormData({
      ...formData,
      life_stories: [
        ...formData.life_stories,
        {
          title: newStory.title,
          content: newStory.content,
        },
      ],
    });

    setNewStory({
      title: "",
      content: "",
    });
  };

  const removeLifeStory = (index) => {
    const updated = [...formData.life_stories];

    updated.splice(index, 1);

    setFormData({
      ...formData,
      life_stories: updated,
    });
  };

  const updateLifeStory = (index, field, value) => {
    const updated = [...formData.life_stories];

    updated[index][field] = value;

    setFormData({
      ...formData,
      life_stories: updated,
    });
  };

  const addReligiousItem = () => {
    if (!newReligious.text && !newReligious.link) {
      alert("İçerik giriniz");
      return;
    }

    setFormData({
      ...formData,
      religious_items: [
        ...formData.religious_items,
        {
          ...newReligious,
        },
      ],
    });

    setNewReligious({
      type: "quran",
      link: "",
      text: "",
    });
  };

  const removeReligiousItem = (index) => {
    const updated = [...formData.religious_items];

    updated.splice(index, 1);

    setFormData({
      ...formData,
      religious_items: updated,
    });
  };

  const addMemory = () => {
    if (!newMemory.title.trim()) {
      alert("Başlık giriniz");
      return;
    }

    setFormData({
      ...formData,
      memories: [
        ...formData.memories,
        {
          ...newMemory,
        },
      ],
    });

    setNewMemory({
      title: "",
      description: "",
      image_url: "",
    });
  };

  const removeMemory = (index) => {
    const updated = [...formData.memories];

    updated.splice(index, 1);

    setFormData({
      ...formData,
      memories: updated,
    });
  };

  const sendCondolence = async () => {
    if (
      !newCondolence.sender_name.trim() ||
      !newCondolence.message.trim()
    ) {
      alert(
        TT.yourName +
          " " +
          (lang === "tr"
            ? "ve mesaj zorunludur"
            : "and message are required"),
      );

      return;
    }

    setSubmittingCondolence(true);

    try {
      const res = await fetch(
        `${API_BASE}/public/cemetery/condolences`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            memorial_id: showDetailModal?.id,
            sender_name: newCondolence.sender_name,
            message: newCondolence.message,
          }),
        },
      );

      const data = await res.json();

      if (data.ok) {
        alert(data.message);

        setNewCondolence({
          sender_name: "",
          message: "",
        });

        fetchMemorials();

        if (showDetailModal) {
          const refreshRes = await fetch(
            `${API_BASE}/cemetery/memorials/${showDetailModal.id}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  "token",
                )}`,
              },
            },
          );

          const refreshData = await refreshRes.json();

          if (refreshData.ok) {
            setShowDetailModal(refreshData.data);
          }
        }
      } else {
        alert(data.message || "Mesaj gönderilemedi");
      }
    } catch (err) {
      console.error(err);
      alert("Bir hata oluştu");
    } finally {
      setSubmittingCondolence(false);
    }
  };

  const getStatusColor = (status) =>
    status === "active"
      ? "bg-green-100 text-green-800"
      : "bg-gray-100 text-gray-800";

  const getStatusText = (status) =>
    status === "active" ? TT.active : TT.inactive;

  const mapEmbed = getGoogleMapsEmbedUrl(
    formData.location_address,
  );

  const getReligiousIcon = (type) => {
    switch (type) {
      case "quran":
        return "📖";

      case "hadith":
        return "📜";

      default:
        return "🙏";
    }
  };

  const getReligiousTypeText = (type) => {
    switch (type) {
      case "quran":
        return TT.religiousTypeQuran;

      case "hadith":
        return TT.religiousTypeHadith;

      default:
        return TT.religiousTypeNote;
    }
  };

  const getCondolenceStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-700";

      case "rejected":
        return "bg-red-100 text-red-700";

      default:
        return "bg-yellow-100 text-yellow-700";
    }
  };

  const getCondolenceStatusText = (status) => {
    switch (status) {
      case "approved":
        return TT.approved;

      case "rejected":
        return TT.rejected;

      default:
        return TT.pendingApproval;
    }
  };

  const dashboardText = {
    tr: {
      subtitle:
        "Dijital anıtları, yaşam hikâyelerini, hatıraları ve anı defterini tek merkezden yönetin.",
      total: "Toplam Anıt",
      active: "Aktif Anıt",
      manage: "Dijital Anıt Yönetimi",
      emptyTitle: "Henüz dijital anıt bulunmuyor",
      emptyDescription:
        "İlk dijital anıt kaydını oluşturarak yaşam hikâyelerini ve hatıraları kalıcı hâle getirin.",
      grave: "Mezar No",
    },

    en: {
      subtitle:
        "Manage digital memorials, life stories, memories and the guest book from one place.",
      total: "Total Memorials",
      active: "Active Memorials",
      manage: "Digital Memorial Management",
      emptyTitle: "No digital memorials yet",
      emptyDescription:
        "Create the first digital memorial to preserve life stories and memories.",
      grave: "Grave No",
    },

    de: {
      subtitle:
        "Verwalten Sie digitale Denkmäler, Lebensgeschichten und Erinnerungen zentral.",
      total: "Denkmäler gesamt",
      active: "Aktive Denkmäler",
      manage: "Digitale Denkmalverwaltung",
      emptyTitle: "Noch keine digitalen Denkmäler",
      emptyDescription:
        "Erstellen Sie das erste digitale Denkmal, um Lebensgeschichten und Erinnerungen zu bewahren.",
      grave: "Grabnummer",
    },
  }[lang];

  return (
    <div className="memorial-page min-h-screen w-full max-w-none overflow-x-hidden bg-slate-100 pb-10">
      <style>{`
        @keyframes memorialFadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes memorialGlow {
          0%, 100% { opacity: .34; transform: scale(1); }
          50% { opacity: .58; transform: scale(1.08); }
        }

        .memorial-card {
          animation: memorialFadeUp .38s ease both;
        }

        .memorial-page input,
        .memorial-page textarea,
        .memorial-page select {
          min-width: 0;
        }

        .memorial-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .memorial-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 999px;
        }

        .memorial-form input,
        .memorial-form textarea,
        .memorial-form select {
          width: 100%;
          border: 1px solid #dbe3ee;
          border-radius: 12px;
          background: #fff;
          padding: .75rem .9rem;
          color: #0f172a;
          outline: none;
          transition:
            border-color .2s ease,
            box-shadow .2s ease,
            background .2s ease;
        }

        .memorial-form .relative input {
          padding-left: 2.5rem;
        }

        .memorial-form input:focus,
        .memorial-form textarea:focus,
        .memorial-form select:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37,99,235,.09);
          background: #fff;
        }

        .memorial-form textarea {
          resize: vertical;
        }
      `}</style>

      <header className="relative w-full overflow-hidden border-b-4 border-orange-500 bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-800 text-white">
        <div className="pointer-events-none absolute -right-32 -top-56 h-[430px] w-[430px] rounded-full bg-white/15 blur-2xl [animation:memorialGlow_5s_ease-in-out_infinite]" />

        <div className="relative z-10 w-full px-4 py-7 sm:px-6 md:px-8 md:py-10">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-4xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-extrabold tracking-wide text-white backdrop-blur-md">
                <Sparkles
                  size={15}
                  className="text-amber-300"
                />

                {dashboardText.manage}
              </div>

              <h1 className="text-3xl font-black leading-tight tracking-tight sm:text-4xl md:text-5xl">
                {TT.title}
              </h1>

              <p className="mt-3 max-w-3xl text-sm leading-6 text-white/70 sm:text-base">
                {dashboardText.subtitle}
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setSelectedMemorial(null);
                resetForm();
                setShowModal(true);
              }}
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3 font-extrabold text-white shadow-xl shadow-orange-950/20 transition hover:-translate-y-0.5 hover:bg-orange-600 sm:w-auto"
            >
              <Plus size={19} />
              {TT.addMemorial}
            </button>
          </div>

          {stats && (
            <div className="mt-7 grid grid-cols-2 gap-3 md:grid-cols-5">
              {[
                {
                  value: stats.toplam_anit || 0,
                  label: dashboardText.total,
                  icon: <BookOpen size={20} />,
                  color:
                    "text-cyan-300 bg-cyan-300/15",
                },

                {
                  value: stats.aktif_anit || 0,
                  label: dashboardText.active,
                  icon: <CheckCircle size={20} />,
                  color:
                    "text-emerald-300 bg-emerald-300/15",
                },

                {
                  value:
                    stats.toplam_goruntulenme || 0,
                  label: TT.viewCount,
                  icon: <Eye size={20} />,
                  color:
                    "text-blue-300 bg-blue-300/15",
                },

                {
                  value: stats.toplam_taziye || 0,
                  label: TT.condolenceCount,
                  icon: <Heart size={20} />,
                  color:
                    "text-rose-300 bg-rose-300/15",
                },

                {
                  value: stats.toplam_medya || 0,
                  label: TT.mediaCount,
                  icon: <ImageIcon size={20} />,
                  color:
                    "text-violet-300 bg-violet-300/15",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="min-w-0 rounded-2xl border border-white/15 bg-white/10 p-3 backdrop-blur-md transition hover:-translate-y-1 hover:bg-white/15 sm:p-4"
                >
                  <div
                    className={`mb-3 grid h-10 w-10 place-items-center rounded-xl ${item.color}`}
                  >
                    {item.icon}
                  </div>

                  <div className="text-2xl font-black leading-none sm:text-3xl">
                    {item.value}
                  </div>

                  <div className="mt-1.5 truncate text-xs font-bold text-white/65 sm:text-sm">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="w-full px-3 pt-4 sm:px-5 md:px-8 md:pt-6">
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_12px_35px_rgba(15,23,42,.06)] sm:p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="grid grid-cols-3 gap-1.5 rounded-xl bg-slate-100 p-1.5 sm:flex sm:w-fit">
              <button
                type="button"
                onClick={() =>
                  setStatusFilter("all")
                }
                className={`min-h-10 rounded-lg px-3 py-2 text-sm font-extrabold transition ${
                  statusFilter === "all"
                    ? "bg-slate-900 text-white shadow-md"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                {TT.all}
              </button>

              <button
                type="button"
                onClick={() =>
                  setStatusFilter("active")
                }
                className={`min-h-10 rounded-lg px-3 py-2 text-sm font-extrabold transition ${
                  statusFilter === "active"
                    ? "bg-emerald-600 text-white shadow-md"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                {TT.active}
              </button>

              <button
                type="button"
                onClick={() =>
                  setStatusFilter("inactive")
                }
                className={`min-h-10 rounded-lg px-3 py-2 text-sm font-extrabold transition ${
                  statusFilter === "inactive"
                    ? "bg-slate-600 text-white shadow-md"
                    : "text-slate-600 hover:bg-white"
                }`}
              >
                {TT.inactive}
              </button>
            </div>

            <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
              <div className="relative min-w-0 sm:w-80">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  placeholder={TT.search}
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                  className="min-h-11 w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-4 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10"
                />
              </div>

              <button
                type="button"
                onClick={() => {
                  setSelectedMemorial(null);
                  resetForm();
                  setShowModal(true);
                }}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-orange-500/20 transition hover:bg-orange-600"
              >
                <Plus size={18} />
                {TT.addMemorial}
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {loading ? (
            <div className="col-span-full grid min-h-72 place-items-center rounded-3xl border border-slate-200 bg-white">
              <div className="text-center">
                <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-orange-500" />

                <div className="font-bold text-slate-500">
                  {TT.loading}
                </div>
              </div>
            </div>
          ) : memorials.length === 0 ? (
            <div className="col-span-full grid min-h-80 place-items-center rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-center">
              <div className="max-w-xl">
                <div className="mx-auto grid h-20 w-20 place-items-center rounded-3xl bg-blue-50 text-blue-600">
                  <BookOpen size={36} />
                </div>

                <h2 className="mt-5 text-xl font-black text-slate-900 sm:text-2xl">
                  {dashboardText.emptyTitle}
                </h2>

                <p className="mt-2 text-sm leading-6 text-slate-500">
                  {dashboardText.emptyDescription}
                </p>
              </div>
            </div>
          ) : (
            memorials.map((memorial) => (
              <div
                key={memorial.id}
                className="memorial-card group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_12px_32px_rgba(15,23,42,.06)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_20px_44px_rgba(15,23,42,.12)]"
              >
                <div className="relative h-24 bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-800">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,.24),transparent_55%)]" />

                  <span
                    className={`absolute right-3 top-3 rounded-full px-2.5 py-1 text-xs font-extrabold shadow-sm ${getStatusColor(
                      memorial.status,
                    )}`}
                  >
                    {getStatusText(memorial.status)}
                  </span>

                  <div className="absolute -bottom-11 left-5 h-24 w-24 rounded-3xl border-4 border-white bg-slate-100 p-0.5 shadow-xl">
                    {memorial.photo_url ? (
                      <img
                        src={memorial.photo_url}
                        alt={memorial.fullname}
                        className="h-full w-full rounded-[18px] object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center rounded-[18px] bg-gradient-to-br from-slate-100 to-slate-200 text-4xl">
                        👤
                      </div>
                    )}
                  </div>
                </div>

                <div className="px-5 pb-4 pt-14">
                  <div className="min-w-0">
                    <h3 className="truncate text-xl font-black tracking-tight text-slate-900">
                      {memorial.fullname}
                    </h3>

                    <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-500">
                      <Calendar
                        size={15}
                        className="shrink-0 text-orange-500"
                      />

                      {memorial.birth_date_text ||
                        memorial.birth_date_picker?.slice(
                          0,
                          4,
                        ) ||
                        "?"}{" "}
                      -{" "}
                      {memorial.death_date_text ||
                        memorial.death_date_picker?.slice(
                          0,
                          4,
                        ) ||
                        "?"}
                    </div>

                    <div className="mt-2 flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-500">
                      <MapPin
                        size={15}
                        className="shrink-0 text-blue-500"
                      />

                      <span className="truncate">
                        {memorial.cemetery_name ||
                          memorial.city ||
                          "—"}
                      </span>
                    </div>

                    {memorial.grave_no && (
                      <div className="mt-2 inline-flex rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-extrabold text-slate-600">
                        {dashboardText.grave}:{" "}
                        {memorial.grave_no}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mx-5 grid grid-cols-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                  <div
                    className="flex flex-col items-center border-r border-slate-200 px-2 py-2.5"
                    title={TT.viewCount}
                  >
                    <Eye
                      size={15}
                      className="text-blue-500"
                    />

                    <span className="mt-1 text-xs font-black text-slate-700">
                      {memorial.view_count || 0}
                    </span>
                  </div>

                  <div
                    className="flex flex-col items-center border-r border-slate-200 px-2 py-2.5"
                    title={TT.condolenceCount}
                  >
                    <Heart
                      size={15}
                      className="text-rose-500"
                    />

                    <span className="mt-1 text-xs font-black text-slate-700">
                      {memorial.condolences_count ||
                        0}
                    </span>
                  </div>

                  <div
                    className="flex flex-col items-center px-2 py-2.5"
                    title={TT.mediaCount}
                  >
                    <ImageIcon
                      size={15}
                      className="text-violet-500"
                    />

                    <span className="mt-1 text-xs font-black text-slate-700">
                      {memorial.memories?.length ||
                        0}
                    </span>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 bg-slate-50/70 px-4 py-3">
                  <button
                    type="button"
                    onClick={() =>
                      handleEdit(memorial)
                    }
                    className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 text-xs font-extrabold text-blue-700 transition hover:bg-blue-100"
                    title={TT.editMemorial}
                  >
                    <Edit size={15} />

                    <span className="hidden sm:inline">
                      {
                        TT.editMemorial.split(
                          " ",
                        )[0]
                      }
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setShowDetailModal(memorial)
                    }
                    className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 text-xs font-extrabold text-emerald-700 transition hover:bg-emerald-100"
                    title={TT.details}
                  >
                    <Eye size={15} />

                    <span className="hidden sm:inline">
                      {TT.details}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      handleDelete(memorial)
                    }
                    className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 text-xs font-extrabold text-rose-700 transition hover:bg-rose-100"
                    title={TT.deleteMemorial}
                  >
                    <Trash2 size={15} />

                    <span className="hidden sm:inline">
                      {TT.deleteMemorial.split(
                        " ",
                      )[1] || TT.deleteMemorial}
                    </span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/75 p-2 backdrop-blur-sm sm:p-4">
          <div className="memorial-scrollbar memorial-card relative max-h-[96vh] w-full max-w-6xl overflow-y-auto rounded-2xl border border-white/20 bg-slate-50 shadow-2xl sm:rounded-3xl">
            <div className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-white/10 bg-gradient-to-r from-slate-950 via-blue-950 to-cyan-800 px-4 py-4 text-white sm:px-6 sm:py-5">
              <div className="min-w-0">
                <div className="mb-1 text-xs font-extrabold uppercase tracking-[.18em] text-orange-300">
                  {dashboardText.manage}
                </div>

                <h2 className="truncate text-lg font-black sm:text-2xl">
                  {selectedMemorial
                    ? TT.editMemorial
                    : TT.addMemorial}
                </h2>
              </div>

              <button
                type="button"
                onClick={() =>
                  setShowModal(false)
                }
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
              >
                <X size={20} />
              </button>
            </div>

            <div className="memorial-form p-3 sm:p-5 md:p-6">
              <div className="memorial-scrollbar mb-5 flex gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
                <button
                  type="button"
                  onClick={() =>
                    setActiveTab("basic")
                  }
                  className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-extrabold transition ${
                    activeTab === "basic"
                      ? "bg-slate-900 text-white shadow-lg"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  📋 {TT.basicInfo}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setActiveTab("stories")
                  }
                  className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-extrabold transition ${
                    activeTab === "stories"
                      ? "bg-orange-500 text-white shadow-lg"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  📖 {TT.lifeStories}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setActiveTab("religious")
                  }
                  className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-extrabold transition ${
                    activeTab === "religious"
                      ? "bg-emerald-600 text-white shadow-lg"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  🕌 {TT.religious}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setActiveTab("memories")
                  }
                  className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-extrabold transition ${
                    activeTab === "memories"
                      ? "bg-rose-600 text-white shadow-lg"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  💖 {TT.inOurHearts}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setActiveTab("location")
                  }
                  className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl px-4 py-2 text-sm font-extrabold transition ${
                    activeTab === "location"
                      ? "bg-blue-600 text-white shadow-lg"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  📍 {TT.location}
                </button>
              </div>

              {activeTab === "basic" && (
                <div className="grid grid-cols-1 gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-2 md:p-6">
                  <div className="flex justify-center md:col-span-2">
                    <div className="relative rounded-3xl bg-gradient-to-br from-blue-50 to-orange-50 p-4 ring-1 ring-slate-200">
                      {formData.photo_url ? (
                        <img
                          src={formData.photo_url}
                          alt={
                            formData.fullname ||
                            TT.photo
                          }
                          className="h-28 w-28 rounded-3xl border-4 border-white object-cover shadow-xl"
                        />
                      ) : (
                        <div className="flex h-28 w-28 items-center justify-center rounded-3xl border-4 border-white bg-gradient-to-br from-slate-100 to-slate-200 text-5xl shadow-xl">
                          👤
                        </div>
                      )}

                      <div className="absolute -bottom-2 -right-2 max-w-[160px]">
                        <ResimUpload
                          onUpload={(url) =>
                            setFormData({
                              ...formData,
                              photo_url: url,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <input
                    type="text"
                    placeholder={TT.fullname}
                    value={formData.fullname}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        fullname: e.target.value,
                      })
                    }
                    className="md:col-span-2"
                  />

                  <div className="rounded-2xl bg-slate-50 p-3 md:col-span-2">
                    <label className="mb-2 block text-sm font-extrabold text-slate-700">
                      {TT.birthDate}
                    </label>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="relative">
                        <Type
                          size={16}
                          className="absolute left-3 top-3 text-gray-400"
                        />

                        <input
                          type="text"
                          placeholder={
                            TT.birthPlaceholder
                          }
                          value={
                            formData.birth_date_text
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              birth_date_text:
                                e.target.value,
                            })
                          }
                        />

                        <span className="ml-1 mt-1 block text-xs font-semibold text-slate-400">
                          {TT.birthDateText}
                        </span>
                      </div>

                      <div className="relative">
                        <Calendar
                          size={16}
                          className="absolute left-3 top-3 text-gray-400"
                        />

                        <input
                          type="date"
                          value={
                            formData.birth_date_picker
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              birth_date_picker:
                                e.target.value,
                            })
                          }
                        />

                        <span className="ml-1 mt-1 block text-xs font-semibold text-slate-400">
                          {TT.birthDatePicker}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl bg-slate-50 p-3 md:col-span-2">
                    <label className="mb-2 block text-sm font-extrabold text-slate-700">
                      {TT.deathDate}
                    </label>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="relative">
                        <Type
                          size={16}
                          className="absolute left-3 top-3 text-gray-400"
                        />

                        <input
                          type="text"
                          placeholder={
                            TT.deathPlaceholder
                          }
                          value={
                            formData.death_date_text
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              death_date_text:
                                e.target.value,
                            })
                          }
                        />

                        <span className="ml-1 mt-1 block text-xs font-semibold text-slate-400">
                          {TT.deathDateText}
                        </span>
                      </div>

                      <div className="relative">
                        <Calendar
                          size={16}
                          className="absolute left-3 top-3 text-gray-400"
                        />

                        <input
                          type="date"
                          value={
                            formData.death_date_picker
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              death_date_picker:
                                e.target.value,
                            })
                          }
                        />

                        <span className="ml-1 mt-1 block text-xs font-semibold text-slate-400">
                          {TT.deathDatePicker}
                        </span>
                      </div>
                    </div>
                  </div>

                  <input
                    type="text"
                    placeholder={TT.city}
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        city: e.target.value,
                      })
                    }
                  />

                  <input
                    type="text"
                    placeholder={TT.cemeteryName}
                    value={formData.cemetery_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cemetery_name:
                          e.target.value,
                      })
                    }
                  />

                  <input
                    type="text"
                    placeholder={TT.graveNo}
                    value={formData.grave_no}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        grave_no: e.target.value,
                      })
                    }
                  />

                  <textarea
                    placeholder={TT.familyNote}
                    value={formData.family_note}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        family_note:
                          e.target.value,
                      })
                    }
                    className="md:col-span-2"
                    rows="2"
                  />

                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.value,
                      })
                    }
                    className="md:col-span-2"
                  >
                    <option value="active">
                      {TT.active}
                    </option>

                    <option value="inactive">
                      {TT.inactive}
                    </option>
                  </select>
                </div>
              )}

              {activeTab === "stories" && (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                    <input
                      type="text"
                      placeholder={TT.storyTitle}
                      value={newStory.title}
                      onChange={(e) =>
                        setNewStory({
                          ...newStory,
                          title: e.target.value,
                        })
                      }
                      className="mb-3"
                    />

                    <textarea
                      placeholder={
                        TT.storyPlaceholder
                      }
                      value={newStory.content}
                      onChange={(e) =>
                        setNewStory({
                          ...newStory,
                          content: e.target.value,
                        })
                      }
                      className="mb-3"
                      rows="4"
                    />

                    <button
                      type="button"
                      onClick={addLifeStory}
                      className="inline-flex min-h-11 items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700"
                    >
                      {TT.addStory}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.life_stories.map(
                      (story, idx) => (
                        <div
                          key={idx}
                          className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                        >
                          <div className="mb-3 flex items-start gap-2">
                            <input
                              type="text"
                              value={story.title}
                              onChange={(e) =>
                                updateLifeStory(
                                  idx,
                                  "title",
                                  e.target.value,
                                )
                              }
                              className="flex-1 text-lg font-extrabold"
                            />

                            <button
                              type="button"
                              onClick={() =>
                                removeLifeStory(
                                  idx,
                                )
                              }
                              className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-rose-200 bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>

                          <textarea
                            value={story.content}
                            onChange={(e) =>
                              updateLifeStory(
                                idx,
                                "content",
                                e.target.value,
                              )
                            }
                            rows="6"
                            placeholder={TT.writeHere}
                          />
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {activeTab === "religious" && (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                    <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                      <select
                        value={newReligious.type}
                        onChange={(e) =>
                          setNewReligious({
                            ...newReligious,
                            type: e.target.value,
                          })
                        }
                      >
                        <option value="quran">
                          {TT.religiousTypeQuran}
                        </option>

                        <option value="hadith">
                          {TT.religiousTypeHadith}
                        </option>

                        <option value="note">
                          {TT.religiousTypeNote}
                        </option>
                      </select>

                      <input
                        type="url"
                        placeholder={
                          TT.religiousLink
                        }
                        value={newReligious.link}
                        onChange={(e) =>
                          setNewReligious({
                            ...newReligious,
                            link: e.target.value,
                          })
                        }
                      />

                      <textarea
                        placeholder={
                          TT.religiousText
                        }
                        value={newReligious.text}
                        onChange={(e) =>
                          setNewReligious({
                            ...newReligious,
                            text: e.target.value,
                          })
                        }
                        rows="2"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={addReligiousItem}
                      className="inline-flex min-h-11 items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700"
                    >
                      {TT.addReligiousItem}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.religious_items.map(
                      (item, idx) => (
                        <div
                          key={idx}
                          className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 font-extrabold text-slate-800">
                              <span>
                                {getReligiousIcon(
                                  item.type,
                                )}
                              </span>

                              <span>
                                {getReligiousTypeText(
                                  item.type,
                                )}
                              </span>
                            </div>

                            {item.link &&
                              (getYouTubeEmbedUrl(
                                item.link,
                              ) ? (
                                <iframe
                                  src={getYouTubeEmbedUrl(
                                    item.link,
                                  )}
                                  className="mt-3 h-56 w-full rounded-2xl border border-slate-200"
                                  allowFullScreen
                                />
                              ) : (
                                <a
                                  href={item.link}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="mt-2 block break-all text-sm font-bold text-blue-600"
                                >
                                  🔗 {item.link}
                                </a>
                              ))}

                            {item.text && (
                              <p className="mt-3 whitespace-pre-wrap rounded-xl bg-slate-50 p-3 italic text-slate-600">
                                {item.text}
                              </p>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              removeReligiousItem(
                                idx,
                              )
                            }
                            className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-rose-200 bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {activeTab === "memories" && (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                    <input
                      type="text"
                      placeholder={TT.memoryTitle}
                      value={newMemory.title}
                      onChange={(e) =>
                        setNewMemory({
                          ...newMemory,
                          title: e.target.value,
                        })
                      }
                      className="mb-3"
                    />

                    <textarea
                      placeholder={
                        TT.memoryDescription
                      }
                      value={
                        newMemory.description
                      }
                      onChange={(e) =>
                        setNewMemory({
                          ...newMemory,
                          description:
                            e.target.value,
                        })
                      }
                      className="mb-3"
                      rows="3"
                    />

                    <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
                      <input
                        type="url"
                        placeholder={TT.memoryImage}
                        value={newMemory.image_url}
                        onChange={(e) =>
                          setNewMemory({
                            ...newMemory,
                            image_url:
                              e.target.value,
                          })
                        }
                        className="min-w-0 flex-1"
                      />

                      <ResimUpload
                        onUpload={(url) =>
                          setNewMemory({
                            ...newMemory,
                            image_url: url,
                          })
                        }
                      />
                    </div>

                    <button
                      type="button"
                      onClick={addMemory}
                      className="mt-3 inline-flex min-h-11 items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700"
                    >
                      {TT.addMemory}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {formData.memories.map(
                      (memory, idx) => (
                        <div
                          key={idx}
                          className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                        >
                          {memory.image_url && (
                            <img
                              src={
                                memory.image_url
                              }
                              alt={memory.title}
                              className="h-48 w-full object-cover"
                            />
                          )}

                          <div className="p-3">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="text-lg font-extrabold text-slate-800">
                                {memory.title}
                              </h4>

                              <button
                                type="button"
                                onClick={() =>
                                  removeMemory(
                                    idx,
                                  )
                                }
                                className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                              >
                                <Trash2
                                  size={16}
                                />
                              </button>
                            </div>

                            <p className="mt-1 text-sm leading-6 text-slate-600">
                              {
                                memory.description
                              }
                            </p>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {activeTab === "location" && (
                <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
                  <div>
                    <label className="mb-2 block text-sm font-extrabold text-slate-700">
                      {TT.locationAddress}
                    </label>

                    <input
                      type="text"
                      placeholder="https://maps.google.com/..."
                      value={
                        formData.location_address
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          location_address:
                            e.target.value,
                        })
                      }
                    />

                    <p className="mt-2 text-xs font-semibold leading-5 text-slate-500">
                      {TT.locationHelp}
                    </p>
                  </div>

                  {mapEmbed && (
                    <div className="mt-4">
                      <label className="mb-2 block text-sm font-extrabold text-slate-700">
                        {TT.mapPreview}
                      </label>

                      <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
                        <iframe
                          src={mapEmbed}
                          className="h-72 w-full"
                          title="Konum"
                          allowFullScreen
                        />
                      </div>

                      <a
                        href={
                          formData.location_address
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 text-sm font-extrabold text-blue-600 hover:text-blue-700"
                      >
                        <MapPin size={14} />
                        {TT.viewOnMap}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 z-20 mt-5 flex flex-col-reverse justify-end gap-2 border-t border-slate-200 bg-white/95 p-3 backdrop-blur-xl sm:flex-row sm:p-4">
              <button
                type="button"
                onClick={() =>
                  setShowModal(false)
                }
                className="min-h-12 rounded-xl border border-slate-200 bg-slate-100 px-5 py-2.5 font-extrabold text-slate-700 transition hover:bg-slate-200"
              >
                {TT.cancel}
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="min-h-12 rounded-xl bg-orange-500 px-6 py-2.5 font-extrabold text-white shadow-lg shadow-orange-500/20 transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? "⏳" : "💾"}{" "}
                {TT.save}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/75 p-2 backdrop-blur-sm sm:p-4">
          <div className="memorial-scrollbar memorial-card max-h-[96vh] w-full max-w-5xl overflow-y-auto rounded-2xl border border-white/20 bg-slate-50 shadow-2xl sm:rounded-3xl">
            <div className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-white/10 bg-gradient-to-r from-slate-950 via-blue-950 to-cyan-800 px-4 py-4 text-white sm:px-6 sm:py-5">
              <div className="min-w-0">
                <div className="mb-1 text-xs font-extrabold uppercase tracking-[.18em] text-orange-300">
                  {TT.details}
                </div>

                <h2 className="truncate text-lg font-black sm:text-2xl">
                  {showDetailModal.fullname}
                </h2>
              </div>

              <button
                type="button"
                onClick={() =>
                  setShowDetailModal(null)
                }
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
              >
                <X size={20} />
              </button>
            </div>

            <div className="memorial-form p-3 sm:p-5 md:p-6">
              <div className="mb-5 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                <div className="h-24 bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-800" />

                <div className="flex flex-col gap-5 px-4 pb-5 sm:px-6 md:flex-row md:items-end">
                  <div className="-mt-14 shrink-0 text-center">
                    {showDetailModal.photo_url ? (
                      <img
                        src={
                          showDetailModal.photo_url
                        }
                        alt={
                          showDetailModal.fullname
                        }
                        className="mx-auto h-32 w-32 rounded-3xl border-4 border-white object-cover shadow-2xl"
                      />
                    ) : (
                      <div className="mx-auto flex h-32 w-32 items-center justify-center rounded-3xl border-4 border-white bg-gradient-to-br from-slate-100 to-slate-200 text-5xl shadow-2xl">
                        👤
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1 pb-1">
                    <h3 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                      {
                        showDetailModal.fullname
                      }
                    </h3>

                    <div className="mt-3 flex flex-wrap gap-2 text-sm font-bold text-slate-600">
                      <span className="inline-flex items-center rounded-xl bg-slate-100 px-3 py-1.5">
                        📅{" "}
                        {showDetailModal.birth_date_text ||
                          showDetailModal.birth_date_picker?.slice(
                            0,
                            4,
                          ) ||
                          "?"}{" "}
                        -{" "}
                        {showDetailModal.death_date_text ||
                          showDetailModal.death_date_picker?.slice(
                            0,
                            4,
                          ) ||
                          "?"}
                      </span>

                      {showDetailModal.city && (
                        <span className="inline-flex items-center rounded-xl bg-blue-50 px-3 py-1.5 text-blue-700">
                          📍{" "}
                          {
                            showDetailModal.city
                          }
                        </span>
                      )}

                      {showDetailModal.cemetery_name && (
                        <span className="inline-flex items-center rounded-xl bg-orange-50 px-3 py-1.5 text-orange-700">
                          🪦{" "}
                          {
                            showDetailModal.cemetery_name
                          }
                        </span>
                      )}
                    </div>

                    {showDetailModal.grave_no && (
                      <p className="mt-2 text-sm font-bold text-slate-500">
                        {dashboardText.grave}:{" "}
                        {
                          showDetailModal.grave_no
                        }
                      </p>
                    )}

                    {showDetailModal.family_note && (
                      <p className="mt-3 rounded-xl border-l-4 border-orange-400 bg-orange-50 p-3 italic leading-6 text-slate-600">
                        "
                        {
                          showDetailModal.family_note
                        }
                        "
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {showDetailModal.life_stories
                ?.length > 0 && (
                <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-black text-orange-700">
                    <BookOpen size={20} />
                    {TT.lifeStories}
                  </h3>

                  <div className="space-y-4">
                    {showDetailModal.life_stories.map(
                      (story, idx) => (
                        <div
                          key={idx}
                          className="rounded-xl border-l-4 border-orange-400 bg-orange-50/60 p-4"
                        >
                          <h4 className="font-extrabold text-slate-800">
                            {story.title}
                          </h4>

                          <p className="mt-2 whitespace-pre-wrap leading-7 text-slate-600">
                            {story.content}
                          </p>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {showDetailModal.religious_items
                ?.length > 0 && (
                <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 shadow-sm sm:p-5">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-black text-emerald-800">
                    🕌 {TT.religious}
                  </h3>

                  <div className="space-y-3">
                    {showDetailModal.religious_items.map(
                      (item, idx) => (
                        <div
                          key={idx}
                          className="rounded-xl border border-emerald-100 bg-white p-4 shadow-sm"
                        >
                          <div className="flex items-center gap-2 font-extrabold text-slate-800">
                            <span>
                              {getReligiousIcon(
                                item.type,
                              )}
                            </span>

                            <span>
                              {getReligiousTypeText(
                                item.type,
                              )}
                            </span>
                          </div>

                          {item.link &&
                            (getYouTubeEmbedUrl(
                              item.link,
                            ) ? (
                              <iframe
                                src={getYouTubeEmbedUrl(
                                  item.link,
                                )}
                                className="mt-3 h-56 w-full rounded-xl border border-slate-200"
                                allowFullScreen
                              />
                            ) : (
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-2 block break-all text-sm font-bold text-blue-600"
                              >
                                🔗 {item.link}
                              </a>
                            ))}

                          {item.text && (
                            <p className="mt-3 whitespace-pre-wrap leading-7 text-slate-600">
                              {item.text}
                            </p>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {showDetailModal.memories
                ?.length > 0 && (
                <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-black text-rose-700">
                    <Heart size={20} />
                    {TT.inOurHearts}
                  </h3>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {showDetailModal.memories.map(
                      (memory, idx) => (
                        <div
                          key={idx}
                          className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                        >
                          {memory.image_url && (
                            <img
                              src={
                                memory.image_url
                              }
                              alt={memory.title}
                              className="h-44 w-full object-cover"
                            />
                          )}

                          <div className="p-3">
                            <h4 className="font-extrabold text-slate-800">
                              {memory.title}
                            </h4>

                            <p className="mt-1 text-sm leading-6 text-slate-600">
                              {
                                memory.description
                              }
                            </p>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {showDetailModal.location_address && (
                <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                  <h3 className="mb-4 flex items-center gap-2 text-lg font-black text-blue-700">
                    <MapPin size={20} />
                    {TT.location}
                  </h3>

                  {getGoogleMapsEmbedUrl(
                    showDetailModal.location_address,
                  ) ? (
                    <iframe
                      src={getGoogleMapsEmbedUrl(
                        showDetailModal.location_address,
                      )}
                      className="h-72 w-full rounded-2xl border border-slate-200"
                      allowFullScreen
                    />
                  ) : (
                    <a
                      href={
                        showDetailModal.location_address
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="break-all font-bold text-blue-600"
                    >
                      {
                        showDetailModal.location_address
                      }
                    </a>
                  )}
                </div>
              )}

              <div className="mb-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <h3 className="mb-4 flex items-center gap-2 text-lg font-black text-rose-700">
                  <Heart size={20} />
                  {TT.condolences}
                </h3>

                <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                  <input
                    type="text"
                    placeholder={TT.yourName}
                    value={
                      newCondolence.sender_name
                    }
                    onChange={(e) =>
                      setNewCondolence({
                        ...newCondolence,
                        sender_name:
                          e.target.value,
                      })
                    }
                    className="mb-2"
                  />

                  <textarea
                    placeholder={
                      TT.condolencePlaceholder
                    }
                    value={newCondolence.message}
                    onChange={(e) =>
                      setNewCondolence({
                        ...newCondolence,
                        message: e.target.value,
                      })
                    }
                    className="mb-2"
                    rows="3"
                  />

                  <button
                    type="button"
                    onClick={sendCondolence}
                    disabled={
                      submittingCondolence
                    }
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-extrabold text-white shadow-lg shadow-orange-500/20 transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Send size={16} />
                    {TT.sendCondolence}
                  </button>
                </div>

                <div className="memorial-scrollbar max-h-96 space-y-3 overflow-y-auto pr-1">
                  {showDetailModal.condolences
                    ?.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-8 text-center font-semibold text-slate-500">
                      Henüz mesaj yok. İlk mesajı
                      siz yazın.
                    </div>
                  ) : (
                    showDetailModal.condolences?.map(
                      (c, idx) => (
                        <div
                          key={idx}
                          className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <span className="font-extrabold text-slate-800">
                                {
                                  c.sender_name
                                }
                              </span>

                              <span className="ml-2 text-xs font-semibold text-slate-400">
                                {new Date(
                                  c.created_at,
                                ).toLocaleDateString()}
                              </span>
                            </div>

                            <div className="flex shrink-0 gap-1">
                              {c.status ===
                                "pending" && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleApproveCondolence(
                                        c.id,
                                        showDetailModal.id,
                                      )
                                    }
                                    className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-50 text-emerald-600 transition hover:bg-emerald-100"
                                    title={
                                      TT.approve
                                    }
                                  >
                                    <CheckCircle
                                      size={16}
                                    />
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRejectCondolence(
                                        c.id,
                                        showDetailModal.id,
                                      )
                                    }
                                    className="grid h-9 w-9 place-items-center rounded-xl bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                                    title={
                                      TT.reject
                                    }
                                  >
                                    <XCircle
                                      size={16}
                                    />
                                  </button>
                                </>
                              )}

                              <button
                                type="button"
                                onClick={() =>
                                  handleDeleteCondolence(
                                    c.id,
                                    showDetailModal.id,
                                  )
                                }
                                className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-slate-200"
                                title={
                                  TT.deleteCondolence
                                }
                              >
                                <Trash2
                                  size={14}
                                />
                              </button>
                            </div>
                          </div>

                          <p className="mt-2 whitespace-pre-wrap leading-6 text-slate-600">
                            {c.message}
                          </p>

                          <div className="mt-2">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-extrabold ${getCondolenceStatusColor(
                                c.status,
                              )}`}
                            >
                              {getCondolenceStatusText(
                                c.status,
                              )}
                            </span>
                          </div>
                        </div>
                      ),
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}